import React, { useState } from "react";
import { motion } from "framer-motion";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { sendLeadEmail } from "../lib/api";

export const LeadMagnet = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter your name and a valid email.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        full_name: name,
        email,
        phone: "—",
        biggest_challenge: "Downloaded lead magnet: 7 Reasons Local Businesses Lose Customers Online.",
        project_details: "Source: Lead magnet PDF download.",
      };
      await sendLeadEmail(payload);
      setDone(true);
      // Trigger download
      const a = document.createElement("a");
      a.href = "/lead-magnet.pdf";
      a.download = "7-Reasons-Local-Businesses-Lose-Customers-Online.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("PDF downloaded! Check your downloads folder.");
    } catch {
      toast.error("Couldn't process. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="lead-magnet"
      data-testid="lead-magnet-section"
      className="relative py-24 sm:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        {/* PDF Mockup */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5 order-2 lg:order-1"
        >
          <div className="relative max-w-xs mx-auto lg:max-w-sm">
            <div className="absolute -inset-6 bg-[#D4AF37]/15 blur-3xl rounded-full pointer-events-none" />
            <motion.div
              animate={{ rotate: [-2, 2, -2], y: [0, -6, 0] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="relative aspect-[3/4] rounded-2xl bg-gradient-to-br from-[#111] to-[#000] border border-[#D4AF37]/40 p-7 shadow-[0_30px_60px_rgba(0,0,0,0.5)]"
            >
              <div className="text-[9px] tracking-[0.2em] uppercase text-[#D4AF37] mb-6">
                Free PDF · 3 pages
              </div>
              <div className="font-display text-2xl text-white leading-tight">
                7 Reasons Local Businesses Lose
                <span className="gold-text italic"> Customers Online</span>
              </div>
              <div className="my-5 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
              <ul className="space-y-2 text-[11px] text-[#B0B0B0]">
                <li>· The 8-second bounce problem</li>
                <li>· Why referrals stop working</li>
                <li>· The "domain trust" multiplier</li>
                <li>· How to fix it in 14 days</li>
              </ul>
              <div className="absolute bottom-6 left-7 right-7 text-[9px] uppercase tracking-wider text-[#737373]">
                By Divy Shetty · Premium Web Dev
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7 order-1 lg:order-2"
        >
          <div className="inline-flex items-center gap-2 glass-gold rounded-full px-3 py-1 mb-5">
            <FileText className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">
              Free download
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-light text-white">
            Get the free <span className="gold-text italic">3-page guide.</span>
          </h2>
          <p className="mt-5 text-[#B0B0B0] max-w-xl">
            The exact mistakes costing Indian SMBs revenue every day — and the simple
            fixes that turn a website into a lead engine.
          </p>

          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="lead-magnet-success"
              className="mt-8 glass rounded-2xl p-6 max-w-md border border-[#D4AF37]/30"
            >
              <div className="text-sm text-white">
                ✓ PDF downloaded. If your browser blocked it,{" "}
                <a
                  href="/lead-magnet.pdf"
                  download
                  className="text-[#D4AF37] underline"
                >
                  click here to download manually
                </a>
                .
              </div>
            </motion.div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="lead-magnet-form"
              className="mt-8 max-w-md flex flex-col sm:flex-row gap-3"
            >
              <input
                data-testid="lm-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name *"
                className="field-focus flex-1 bg-transparent border border-[#222] focus:border-[#D4AF37] rounded-xl px-4 py-3 outline-none text-white placeholder:text-[#555] text-sm transition-all"
              />
              <input
                data-testid="lm-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email *"
                className="field-focus flex-1 bg-transparent border border-[#222] focus:border-[#D4AF37] rounded-xl px-4 py-3 outline-none text-white placeholder:text-[#555] text-sm transition-all"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="lm-submit"
                className="btn-gold-glow inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E6C35C] disabled:opacity-60 text-black font-semibold rounded-xl px-5 py-3 transition-all whitespace-nowrap"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Download className="w-4 h-4" /> Get PDF
                  </>
                )}
              </button>
            </form>
          )}
          <p className="mt-3 text-[11px] text-[#737373]">
            No spam. Unsubscribe anytime.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
